/**
 * Export Utilities for Matrix Filter
 *
 * Provides PDF and DOCX report generation for hexagon classification results.
 * Includes map canvas capture, data preparation, and document generation.
 *
 * Features:
 * - Map screenshot capture from OpenLayers canvas
 * - PDF generation with light/dark theme support
 * - DOCX generation using docx library
 * - XSS sanitization for user-provided content
 *
 * @module utils
 */

import {
	Document,
	Packer,
	Paragraph,
	Table as DocxTable,
	TableRow,
	TableCell,
	TextRun,
	WidthType,
	HeadingLevel,
	ImageRun,
} from 'docx';
import { saveAs } from 'file-saver';
import { drawScaleBar } from '@utils/map/scaleBar';
import { getGsdInCm } from '@utils/gsd';
import {
	describeClassificationMethod,
	describeCoverageGaps,
	formatCurrencyTotal,
} from './matrixClassification';
import { MODEL_REPORT_LABELS } from './reportLabels';

/**
 * Sanitize string for safe HTML insertion (XSS prevention)
 * @param {string} str - Input string to sanitize
 * @returns {string} Sanitized string with HTML entities escaped
 */
export const escapeHtml = str => {
	if (typeof str !== 'string') return str;
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
};

/**
 * Get theme-specific CSS styles for PDF export
 * @param {'light'|'dark'} theme - Theme name
 * @returns {Object} CSS color values for the theme
 */
export const getThemeStyles = theme => {
	if (theme === 'light') {
		return {
			bodyBg: '#ffffff',
			bodyColor: '#1a1a2e',
			headerColor: '#000000',
			h2Color: '#14743B',
			headerInfoColor: '#666666',
			infoBg: '#f5f5f5',
			infoLabelColor: '#666666',
			infoValueColor: '#000000',
			tableBg: '#f5f5f5',
			tableHeaderBg: '#e0e0e0',
			tableHeaderColor: '#000000',
			tableRowBg: '#f5f5f5',
			tableRowAltBg: '#ebebeb',
			tableBorderColor: '#cccccc',
			footerColor: '#999999',
			footerBorderColor: '#cccccc',
			// Contrast vs bodyBg (#ffffff): ~5.0:1, passes WCAG AA for small text
			warningColor: '#b45309',
		};
	}
	return {
		bodyBg: '#1a1a2e',
		bodyColor: '#e4e4e4',
		headerColor: '#ffffff',
		h2Color: '#14743B',
		headerInfoColor: '#888888',
		infoBg: '#16213e',
		infoLabelColor: '#888888',
		infoValueColor: '#ffffff',
		tableBg: '#16213e',
		tableHeaderBg: '#0f3460',
		tableHeaderColor: '#ffffff',
		tableRowBg: '#16213e',
		tableRowAltBg: '#1a1a3e',
		tableBorderColor: '#333333',
		footerColor: '#666666',
		footerBorderColor: '#333333',
		// The light theme's #b45309 only reaches ~3.4:1 against bodyBg
		// (#1a1a2e), below the 4.5:1 WCAG AA minimum for small text. This
		// lighter amber reaches ~10:1 against the same background.
		warningColor: '#fbbf24',
	};
};

/**
 * Capture OpenLayers map canvas and return as data URL.
 *
 * Composites all visible map layer canvases into a single image.
 * Handles both Canvas2D layers (canvas inside .ol-layer div) and
 * WebGL layers (canvas element with .ol-layer class directly).
 *
 * Before capture, detection point layers are temporarily raised above
 * the hexagon layer (zIndex 20, defined in utils/map/layer/hexagon.js)
 * so they appear on top in the exported image. The original zIndex values
 * are restored immediately after capture.
 *
 * @param {import('ol/Map').default} [map] - OpenLayers map instance (optional)
 * @returns {string|null} PNG data URL or null if capture fails
 */

// Detection layer IDs from DetectionResults component
const DETECTION_LAYER_IDS = ['centerLayer', 'outlineLayer', 'bounding_boxLayer'];

// Must be higher than the hexagon layer zIndex (20, see utils/map/layer/hexagon.js)
const DETECTION_EXPORT_ZINDEX = 25;

export const captureMapCanvas = map => {
	const boostedLayers = [];

	try {
		// Temporarily raise visible detection layers above hexagons for capture
		if (map) {
			map.getLayers()
				.getArray()
				.forEach(layer => {
					const customId = layer.get('customLayerId');
					if (DETECTION_LAYER_IDS.includes(customId) && layer.getVisible()) {
						boostedLayers.push({ layer, originalZIndex: layer.getZIndex() });
						layer.setZIndex(DETECTION_EXPORT_ZINDEX);
					}
				});

			// Re-render with detection layers on top
			map.renderSync();
		}

		const mapCanvas = document.createElement('canvas');

		// Select both Canvas2D layers (canvas inside .ol-layer div) and
		// WebGL layers (canvas element that IS the .ol-layer element)
		// Scope to the provided map's viewport to avoid capturing canvases from other OL instances.
		const captureRoot = map?.getViewport?.() ?? document;
		const olCanvases = captureRoot.querySelectorAll('.ol-layer canvas, canvas.ol-layer');

		let result = null;
		if (olCanvases.length > 0) {
			const firstCanvas = olCanvases[0];
			mapCanvas.width = firstCanvas.width;
			mapCanvas.height = firstCanvas.height;
			const mapContext = mapCanvas.getContext('2d');

			olCanvases.forEach(canvas => {
				if (canvas.width > 0) {
					mapContext.save();
					// For WebGL canvases (canvas.ol-layer), opacity is on the canvas itself
					// For Canvas2D canvases, opacity is on the parent .ol-layer div
					const opacityElement = canvas.classList.contains('ol-layer')
						? canvas
						: canvas.parentNode;
					const opacity = opacityElement.style.opacity;
					mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
					const transform = canvas.style.transform;
					if (transform) {
						const match = transform.match(/^matrix\(([^(]*)\)$/);
						if (match) {
							const matrix = match[1].split(',').map(Number);
							mapContext.setTransform(...matrix);
						}
					} else {
						mapContext.setTransform(1, 0, 0, 1, 0, 0);
					}
					mapContext.drawImage(canvas, 0, 0);
					mapContext.restore();
				}
			});

			mapContext.globalAlpha = 1;
			mapContext.setTransform(1, 0, 0, 1, 0, 0);

			// Draw scale bar overlay onto the composited map canvas
			drawScaleBar(mapCanvas, map);

			result = mapCanvas.toDataURL('image/png');
		}

		return result;
	} catch (e) {
		console.warn('Could not capture map:', e);
		return null;
	} finally {
		// Always restore original zIndex values and re-render to reset visual state
		if (boostedLayers.length > 0) {
			for (const { layer, originalZIndex } of boostedLayers) {
				layer.setZIndex(originalZIndex);
			}
			if (map) map.renderSync();
		}
	}
};

/**
 * Capture OpenLayers map canvas and return as Uint8Array buffer.
 * Used for embedding map images in DOCX documents.
 * Delegates to captureMapCanvas for the actual capture.
 *
 * @param {import('ol/Map').default} [map] - OpenLayers map instance (optional)
 * @returns {Uint8Array|null} PNG buffer or null if capture fails
 */
export const captureMapCanvasAsBuffer = map => {
	try {
		const dataUrl = captureMapCanvas(map);
		if (dataUrl) {
			const base64Data = dataUrl.split(',')[1];
			return Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
		}
	} catch (e) {
		console.warn('Could not capture map for DOCX:', e);
	}
	return null;
};

/**
 * Prepare export data from component state
 * Aggregates classification results, project metadata, and detection statistics.
 *
 * @param {Object} params
 * @param {Object} params.filter - Current filter configuration
 * @param {Object} params.classificationResults - Classification results by class ID
 * @param {Object} params.project - Project metadata
 * @param {Object} params.currentUser - Current user info
 * @param {Object} params.pickedTask - Selected AI task/model
 * @param {Object} params.hexagonInfo - Hexagon metadata summary
 * @param {number} params.hexagonSize - Hexagon size in m²
 * @param {string} params.projectNotes - User-provided notes
 * @param {Object} params.currency - Currency configuration
 * @param {Array} params.availableProperties - Property catalog used to resolve
 *   human-readable labels for filter.property/bandProperty/thresholdProperty
 * @param {Object} params.coverageGaps - Result of getSimpleFilterCoverageGaps
 *   or getMatrixFilterCoverageGaps (whichever matches filter.type), computed
 *   by the caller so the report reflects the same gaps shown in the editor
 * @returns {Object} Prepared export data
 */
export const prepareExportData = ({
	filter,
	classificationResults,
	project,
	currentUser,
	pickedTask,
	hexagonInfo,
	hexagonSize,
	projectNotes,
	currency,
	availableProperties,
	coverageGaps,
}) => {
	// Calculate summary data
	const summaryData = filter.classes.map(cls => {
		const r = classificationResults[cls.id];
		const area = r?.totalArea || 0;
		const price = cls.pricePerM2 || 0;
		return {
			label: cls.label,
			color: cls.color,
			count: r?.count || 0,
			area,
			percentage: r?.areaPercentage || 0,
			pricePerM2: price,
			totalCost: area * price,
		};
	});

	const classificationCost = summaryData.reduce((sum, s) => sum + s.totalCost, 0);
	const totalArea = summaryData.reduce((sum, s) => sum + s.area, 0);

	// Custom price fields — each applied to total area as an additional cost line
	const customPriceFields = (filter.customPriceFields || []).map(f => ({
		label: f.label,
		price: f.price || 0,
		cost: totalArea * (f.price || 0),
	}));
	const customPriceTotalCost = customPriceFields.reduce((sum, f) => sum + f.cost, 0);
	const totalCost = classificationCost + customPriceTotalCost;
	const projectTitle = project?.title || 'Classification Report';

	// Get project metadata
	const projectArea = project?.area > 0 ? (project.area / 10000).toFixed(2) : null; // Convert m² to hectares
	const projectCreated = project?.created_at
		? new Date(project.created_at).toLocaleDateString()
		: null;
	const userName = currentUser?.userName || currentUser?.email || 'Unknown';
	const modelClasses =
		pickedTask?.classes
			?.map(c => {
				// Extract just the class name, removing any multimodel prefix
				const name = c.description || c.class || c.label || c;
				// If it contains " - " (multimodel format), take only the class part
				return typeof name === 'string' && name.includes(' - ')
					? name.split(' - ').pop()
					: name;
			})
			.join(', ') || 'N/A';
	const modelDescription = pickedTask?.description || 'N/A';

	// Get detection statistics from hexagonInfo
	const hexagonsSummary = hexagonInfo?.metadata?.total;
	const totalObjectCount = hexagonsSummary?.metadata?.count || 0;
	const detections = hexagonsSummary?.detections || {};

	// Calculate mean height across all detections and build per-class stats
	let totalHeightSum = 0;
	let totalHeightCount = 0;
	const detectionStats = Object.entries(detections).map(([className, det]) => {
		if (det.mean_height && det.count) {
			totalHeightSum += det.mean_height * det.count;
			totalHeightCount += det.count;
		}
		const percentage =
			totalObjectCount > 0 ? ((det.count / totalObjectCount) * 100).toFixed(1) : 0;
		return {
			name: className,
			count: det.count || 0,
			meanHeight: det.mean_height ? det.mean_height.toFixed(2) : null,
			percentage,
		};
	});
	const overallMeanHeight =
		totalHeightCount > 0 ? (totalHeightSum / totalHeightCount).toFixed(2) : null;

	// cm/px, matching the Layers panel summary (the API sends metres).
	const resolution = getGsdInCm(project);

	// Describe what the classes actually represent (e.g. "Ha/eq (Total)" or
	// "Mean Height (bands) × Density (thresholds)") and, if the configured
	// classes don't cover the full data range, a plain-language note about
	// it. Both are shown directly above the Classification Summary table so
	// the report is self-explanatory without the in-app editor for context.
	const classificationMethod = describeClassificationMethod(filter, availableProperties);
	const coverageGapMessage = describeCoverageGaps(filter, coverageGaps);

	return {
		summaryData,
		totalCost,
		customPriceFields,
		customPriceTotalCost,
		totalArea,
		projectTitle,
		projectArea,
		projectCreated,
		userName,
		modelClasses,
		modelDescription,
		totalObjectCount,
		detectionStats,
		overallMeanHeight,
		resolution,
		hexagonSize,
		projectNotes,
		currency,
		classificationMethod,
		coverageGapMessage,
		labels: MODEL_REPORT_LABELS,
	};
};

/**
 * Generate HTML content for PDF export
 * Creates a printable HTML document with classification results and map.
 *
 * @param {Object} exportData - Prepared export data from prepareExportData
 * @param {'light'|'dark'} theme - Color theme for the PDF
 * @param {string|null} mapImageData - Map screenshot as data URL
 * @returns {string} Complete HTML document string
 */
export const generatePDFContent = (exportData, theme, mapImageData) => {
	const styles = getThemeStyles(theme);
	const {
		summaryData,
		totalCost,
		customPriceFields,
		totalArea,
		projectTitle,
		projectArea,
		projectCreated,
		userName,
		modelClasses,
		modelDescription,
		totalObjectCount,
		detectionStats,
		overallMeanHeight,
		resolution,
		hexagonSize,
		projectNotes,
		currency,
		classificationMethod,
		coverageGapMessage,
		labels = MODEL_REPORT_LABELS,
		// Optional — set by the collection adapters, absent on the model path.
		pricingBasisNote,
		// Optional header for the share column; the collection adapters name what
		// the share is measured against ("% of area" / "% of plots") because their
		// `area` column holds a different quantity than the share beside it.
		percentageLabel = '%',
		// Optional header for the priced area column. Adapters whose `area` holds
		// the detected object area rather than the container area rename it, so the
		// column says which of the two quantities it prints.
		areaLabel = 'Area (m²)',
		// Optional extra area column, rendered before the priced one and only when
		// an adapter supplies its header. The collection adapters classify cells on
		// a container area (hexagon cell size) but price the detected object area
		// inside it, so their report needs both figures side by side — the same
		// "Cell area" + "Object area" pair the panel's results table shows. Reads
		// `row.containerArea`. The model path leaves this null: its single `area`
		// column already IS the container area, so its table renders unchanged.
		containerAreaLabel = null,
		totalContainerArea = 0,
	} = exportData;

	// Blank filler for the container column on the custom-price and TOTAL rows'
	// non-area cells — kept as one value so the row templates below stay readable
	// and can never disagree on the column count.
	const containerAreaGapCell = containerAreaLabel ? '<td></td>' : '';

	return `
		<!DOCTYPE html>
		<html>
		<head>
			<title>${escapeHtml(projectTitle)}</title>
			<style>
				* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
				body {
					font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
					padding: 15px 20px;
					max-width: 100%;
					margin: 0;
					background: ${styles.bodyBg} !important;
					color: ${styles.bodyColor} !important;
					font-size: 11px;
				}
				h1 {
					color: ${styles.headerColor} !important;
					border-bottom: 2px solid #14743B;
					padding-bottom: 8px;
					margin: 0 0 5px 0;
					font-size: 18px;
				}
				h2 {
					color: ${styles.h2Color} !important;
					margin: 12px 0 8px 0;
					font-size: 13px;
				}
				.header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 10px;
				}
				.header-info {
					color: ${styles.headerInfoColor} !important;
					font-size: 10px;
				}
				.logo-text {
					font-size: 16px;
					font-weight: 700;
					color: #14743B !important;
				}
				.content-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 15px;
					margin-bottom: 10px;
				}
				.map-image {
					width: 100%;
					max-height: 280px;
					object-fit: contain;
					border: 2px solid #14743B;
					border-radius: 6px;
				}
				.info-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 8px;
					padding: 10px;
					background: ${styles.infoBg} !important;
					border-radius: 6px;
					border-left: 3px solid #14743B;
					align-content: start;
				}
				.info-item { }
				.info-label {
					font-size: 9px;
					color: ${styles.infoLabelColor} !important;
					text-transform: uppercase;
					letter-spacing: 0.3px;
				}
				.info-value {
					font-size: 11px;
					color: ${styles.infoValueColor} !important;
					font-weight: 500;
				}
				.notes-section {
					margin: 15px 0;
					padding: 12px;
					background: ${styles.infoBg} !important;
					border-radius: 6px;
					border-left: 3px solid #14743B;
				}
				.notes-section p {
					margin: 0;
					white-space: pre-wrap;
					color: ${styles.bodyColor} !important;
				}
				.classification-method {
					font-size: 10px;
					color: ${styles.infoLabelColor} !important;
					margin: 0 0 6px 0;
				}
				.coverage-gap-note {
					font-size: 10px;
					color: ${styles.warningColor} !important;
					margin: 0 0 8px 0;
				}
				table {
					width: 100%;
					border-collapse: collapse;
					background: ${styles.tableBg} !important;
					border-radius: 6px;
					overflow: hidden;
					font-size: 10px;
				}
				th, td {
					padding: 6px 8px;
					text-align: left;
					border-bottom-width: 1px;
					border-bottom-style: solid;
					border-bottom-color: ${styles.tableBorderColor};
				}
				th {
					background: ${styles.tableHeaderBg} !important;
					font-weight: 600;
					color: ${styles.tableHeaderColor} !important;
					font-size: 9px;
				}
				tbody tr { background: ${styles.tableRowBg} !important; }
				tbody tr:nth-child(even) { background: ${styles.tableRowAltBg} !important; }
				.color-dot {
					display: inline-block;
					width: 12px;
					height: 12px;
					border-radius: 3px;
					margin-right: 6px;
					vertical-align: middle;
				}
				.total-row {
					background: #14743B !important;
					color: white !important;
					font-weight: bold;
				}
				tbody tr.total-row { background: #14743B !important; }
				tbody tr.total-row td { border-bottom: none; color: white !important; }
				.currency { font-size: 0.85em; opacity: 0.8; }
				.footer {
					margin-top: 15px;
					padding-top: 10px;
					border-top-width: 1px;
					border-top-style: solid;
					border-top-color: ${styles.footerBorderColor};
					text-align: center;
					color: ${styles.footerColor} !important;
					font-size: 9px;
				}
				.footer p { margin: 2px 0; }
				@media print {
					body {
						background: ${styles.bodyBg} !important;
						color: ${styles.bodyColor} !important;
						-webkit-print-color-adjust: exact !important;
						print-color-adjust: exact !important;
					}
					.no-print { display: none !important; }
					@page { margin: 10mm; size: A4; }
					@page { @top-left { content: none; } @top-right { content: none; } @bottom-left { content: none; } @bottom-right { content: none; } }
				}
			</style>
		</head>
		<body>
			<div class="header">
				<svg viewBox="0 0 500.6 148.9" style="height: 36px; width: auto;" xmlns="http://www.w3.org/2000/svg">
					<style>.st0{fill:none;stroke:#14743B;stroke-width:5;stroke-miterlimit:10;}.st1{fill:#14743B;}.st2{fill:${theme === 'light' ? '#000000' : '#FFFFFF'};}</style>
					<polygon class="st0" points="130.4,142.6 90,146.3 66.6,113.2 83.6,76.4 123.9,72.7 147.3,105.7"/>
					<polygon class="st0" points="66.7,113 26.3,116.8 2.9,83.7 19.8,46.8 60.2,43.1 83.6,76.2"/>
					<polygon class="st0" points="124.6,72.5 84.2,76.3 60.8,43.2 77.8,6.4 118.1,2.6 141.5,35.7"/>
					<polygon class="st1" points="118.2,63.4 88.9,66.2 71.9,42.2 84.2,15.4 113.5,12.7 130.5,36.7"/>
					<path class="st2" d="M211.9,64.7c3.3,3.3,5,7.4,5,12.1c0,4.7-1.7,8.8-5,12.1c-2.8,2.8-6.2,4.4-10.3,5h-17.2V43.3h15.8 c3.2,0,5.9,1.1,8.1,3.3c2.2,2.2,3.3,4.9,3.3,8.1c0,3.1-0.9,5.6-2.8,7.5C209.4,62.2,210.4,63.1,211.9,64.7z M190.6,49.4v10.3h10.2 c1.5-0.1,2.5-0.6,3.2-1.2c1-1,1.5-2.3,1.5-3.7S205,52,204,51c-1-1-2.3-1.6-3.7-1.6H190.6z M199.8,87.8c3,0,5.6-1.1,7.7-3.2 c2.1-2.1,3.2-4.7,3.2-7.8c0-3-1.1-5.6-3.2-7.8c-2.1-2.2-4.7-3.2-7.7-3.2h-9.2v22H199.8z"/>
					<path class="st2" d="M229.4,43.3v50.6h-6.1V43.3H229.4z"/>
					<path class="st2" d="M280.3,50.2c5.1,5.1,7.6,11.2,7.6,18.3c0,7.2-2.5,13.3-7.6,18.4c-5.1,5.1-11.2,7.6-18.3,7.6 c-7.1,0-13.2-2.5-18.3-7.6c-5.1-5.1-7.6-11.2-7.6-18.4c0-7.1,2.5-13.2,7.6-18.3c5.1-5.1,11.2-7.6,18.3-7.6 C269.1,42.6,275.2,45.2,280.3,50.2z M261.9,48.8c-5.4,0-10.1,1.9-14,5.8c-3.9,3.9-5.8,8.5-5.8,13.9c0,5.5,1.9,10.1,5.8,14 c3.9,3.9,8.5,5.8,14,5.8c5.5,0,10.1-1.9,14-5.8c3.9-3.9,5.8-8.5,5.8-14c0-5.4-1.9-10.1-5.8-13.9S267.4,48.8,261.9,48.8z"/>
					<path class="st2" d="M304.6,43.3c8.3,0,14.7,2.4,19.3,7c5,5.1,7.6,11.2,7.6,18.3c0,7.2-2.5,13.3-7.6,18.3c-4.6,4.6-11,6.9-19.3,7 h-10.5V43.3H304.6z M300.3,49.5v38.2h4.3c6.3,0,11.3-1.7,14.8-5.2c3.8-3.8,5.8-8.5,5.8-13.9c0-5.4-1.9-10.1-5.8-13.9 c-3.6-3.4-8.5-5.1-14.8-5.1H300.3z"/>
					<path class="st2" d="M350.1,77.6l0.1,0h-6.3v16.3h-6.1V43.3H352c4.1,0.6,7.5,2.2,10.3,5c3.3,3.4,5,7.4,5,12.1 c0,4.8-1.7,8.8-5,12.1c-2.3,2.3-4.2,3.7-5.6,4l11.3,17.4h-7.4L350.1,77.6z M343.9,49.5v21.9h6.3c3,0,5.6-1.1,7.7-3.2 c2.1-2.1,3.2-4.7,3.2-7.8c0-3-1.1-5.6-3.2-7.7c-2.1-2.1-4.7-3.2-7.7-3.2H343.9z"/>
					<path class="st2" d="M416.8,50.2c5.1,5.1,7.6,11.2,7.6,18.3c0,7.2-2.5,13.3-7.6,18.4c-5.1,5.1-11.2,7.6-18.3,7.6 c-7.1,0-13.2-2.5-18.3-7.6c-5.1-5.1-7.6-11.2-7.6-18.4c0-7.1,2.5-13.2,7.6-18.3c5.1-5.1,11.2-7.6,18.3-7.6 C405.6,42.6,411.8,45.2,416.8,50.2z M398.5,48.8c-5.4,0-10.1,1.9-14,5.8c-3.9,3.9-5.8,8.5-5.8,13.9c0,5.5,1.9,10.1,5.8,14 c3.9,3.9,8.5,5.8,14,5.8c5.5,0,10.1-1.9,14-5.8c3.9-3.9,5.8-8.5,5.8-14c0-5.4-1.9-10.1-5.8-13.9S403.9,48.8,398.5,48.8z"/>
					<path class="st2" d="M459.4,79.3v-36h6.1v55.2L436.7,58v35.9h-6.1v-55L459.4,79.3z"/>
					<path class="st2" d="M500.6,43.3v6.1h-20.9v16.1h20.9v6.2h-20.9v16.1h20.9v6.1h-27V43.3H500.6z"/>
				</svg>
				<div class="header-info">
					<div>Generated: ${new Date().toLocaleString()}</div>
					<div>Analyst: ${escapeHtml(userName)}</div>
				</div>
			</div>

			<h1>${escapeHtml(projectTitle)}</h1>

			<div class="content-grid">
				<div>
					${mapImageData ? `<img src="${mapImageData}" class="map-image" alt="Classification Map" />` : `<div style="background:${styles.infoBg};height:280px;border-radius:6px;display:flex;align-items:center;justify-content:center;color:${styles.footerColor}">No map preview</div>`}
				</div>
				<div class="info-grid">
					<div class="info-item">
						<div class="info-label">Project Area</div>
						<div class="info-value">${projectArea ? projectArea + ' ha' : 'N/A'}</div>
					</div>
					<div class="info-item">
						<div class="info-label">Data Collection</div>
						<div class="info-value">${projectCreated || 'N/A'}</div>
					</div>
					<div class="info-item">
						<div class="info-label">Hexagon Size</div>
						<div class="info-value">${hexagonSize.toLocaleString()} m²</div>
					</div>
					<div class="info-item">
						<div class="info-label">Resolution</div>
						<div class="info-value">${resolution ? resolution + ' cm/px' : 'N/A'}</div>
					</div>
					<div class="info-item">
						<div class="info-label">Objects Detected</div>
						<div class="info-value">${totalObjectCount.toLocaleString()}</div>
					</div>
					<div class="info-item">
						<div class="info-label">Mean Height</div>
						<div class="info-value">${overallMeanHeight ? overallMeanHeight + ' m' : 'N/A'}</div>
					</div>
					<div class="info-item">
						<div class="info-label">Detection Source</div>
						<div class="info-value">${escapeHtml(modelDescription)}</div>
					</div>
					<div class="info-item">
						<div class="info-label">${labels.detectionClasses}</div>
						<div class="info-value">${escapeHtml(modelClasses)}</div>
					</div>
				</div>
			</div>

			${
				projectNotes
					? `
			<h2>Notes</h2>
			<div class="notes-section">
				<p>${escapeHtml(projectNotes)}</p>
			</div>
			`
					: ''
			}

			${
				detectionStats.length > 0
					? `
			<h2>Detection Statistics</h2>
			<table>
				<thead>
					<tr>
						<th>${labels.detectionClass}</th>
						<th>Count</th>
						<th>Distribution</th>
						<th>Mean Height</th>
					</tr>
				</thead>
				<tbody>
					${detectionStats
						.map(
							stat => `
						<tr>
							<td style="text-transform: capitalize">${escapeHtml(stat.name)}</td>
							<td>${stat.count.toLocaleString()}</td>
							<td>${stat.percentage}%</td>
							<td>${stat.meanHeight ? stat.meanHeight + ' m' : '-'}</td>
						</tr>
					`
						)
						.join('')}
					<tr class="total-row">
						<td><strong>TOTAL</strong></td>
						<td><strong>${totalObjectCount.toLocaleString()}</strong></td>
						<td><strong>100%</strong></td>
						<td><strong>${overallMeanHeight ? overallMeanHeight + ' m' : '-'}</strong></td>
					</tr>
				</tbody>
			</table>
			`
					: ''
			}

			<h2>Classification Summary</h2>
			<p class="classification-method">Classified by: ${escapeHtml(classificationMethod)}</p>
			${pricingBasisNote ? `<p class="classification-method">${escapeHtml(pricingBasisNote)}</p>` : ''}
			${coverageGapMessage ? `<p class="coverage-gap-note">⚠ ${escapeHtml(coverageGapMessage)}</p>` : ''}
			<table>
				<thead>
					<tr>
						<th>Class</th>
						<th>Count</th>
						${containerAreaLabel ? `<th>${escapeHtml(containerAreaLabel)}</th>` : ''}
						<th>${escapeHtml(areaLabel)}</th>
						<th>${escapeHtml(percentageLabel)}</th>
						<th>Price/m²</th>
						<th>Total</th>
					</tr>
				</thead>
				<tbody>
					${summaryData
						.map(
							row => `
						<tr>
							<td><span class="color-dot" style="background: ${escapeHtml(row.color)}"></span>${escapeHtml(row.label)}</td>
							<td>${row.count.toLocaleString()}</td>
							${containerAreaLabel ? `<td>${(row.containerArea || 0).toLocaleString()}</td>` : ''}
							<td>${row.area.toLocaleString()}</td>
							<td>${row.percentage}%</td>
							<td>${row.pricePerM2.toFixed(2)} <span class="currency">${escapeHtml(currency.symbol)}</span></td>
							<td>${row.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span class="currency">${escapeHtml(currency.symbol)}</span></td>
						</tr>
					`
						)
						.join('')}
					${customPriceFields
						.filter(f => f.price > 0)
						.map(
							f => `
					<tr style="background: ${styles.tableRowAltBg} !important; font-style: italic;">
						<td colspan="2"><em>${escapeHtml(f.label)}</em></td>
						${containerAreaGapCell}
						<td>${totalArea.toLocaleString()}</td>
						<td></td>
						<td>${f.price.toFixed(2)} <span class="currency">${escapeHtml(currency.symbol)}</span></td>
						<td>${f.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span class="currency">${escapeHtml(currency.symbol)}</span></td>
					</tr>
					`
						)
						.join('')}
					<tr class="total-row">
						<td colspan="2"><strong>TOTAL</strong></td>
						${containerAreaLabel ? `<td><strong>${totalContainerArea.toLocaleString()}</strong></td>` : ''}
						<td><strong>${totalArea.toLocaleString()}</strong></td>
						<td></td>
						<td></td>
						<td><strong>${escapeHtml(formatCurrencyTotal(totalCost, currency))}</strong></td>
					</tr>
				</tbody>
			</table>

			<div class="footer">
				<p>Generated by Biodrone AI Portal</p>
				<p>© ${new Date().getFullYear()} Biodrone AS - www.biodrone.no</p>
			</div>

			<p class="no-print" style="margin-top: 20px; text-align: center;">
				<button onclick="window.print()" style="padding: 10px 20px; background: #14743B; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600;">
					Print / Save as PDF
				</button>
			</p>
		</body>
		</html>
	`;
};

/**
 * Generate DOCX document object
 * Creates a Word document with classification results and optional map image.
 *
 * @param {Object} exportData - Prepared export data from prepareExportData
 * @param {Uint8Array|null} mapImageBuffer - Map screenshot as buffer
 * @returns {Promise<Document>} docx Document object
 */
export const generateDOCXDocument = async (exportData, mapImageBuffer) => {
	const {
		summaryData,
		totalCost,
		customPriceFields,
		totalArea,
		projectTitle,
		projectArea,
		projectCreated,
		userName,
		modelClasses,
		modelDescription,
		totalObjectCount,
		detectionStats,
		overallMeanHeight,
		resolution,
		hexagonSize,
		projectNotes,
		currency,
		classificationMethod,
		coverageGapMessage,
		labels = MODEL_REPORT_LABELS,
		// Optional — set by the collection adapters, absent on the model path.
		pricingBasisNote,
		// Optional header for the share column; the collection adapters name what
		// the share is measured against ("% of area" / "% of plots") because their
		// `area` column holds a different quantity than the share beside it.
		percentageLabel = '%',
		// Optional headers for the area column(s) — see generatePDFContent for why
		// the collection adapters print a container area beside the priced one.
		areaLabel = 'Area (m²)',
		containerAreaLabel = null,
		totalContainerArea = 0,
	} = exportData;

	// docx requires every row of a table to carry the same number of cells, so the
	// optional container column is spread in from these helpers rather than being
	// conditionally inlined per row — one definition, no way for the header, body,
	// custom-price and TOTAL rows to drift out of alignment. `build` is a factory
	// so no cell is constructed at all when the column is off.
	const containerAreaCells = build => (containerAreaLabel ? build() : []);
	const containerAreaGapCells = () =>
		containerAreaCells(() => [new TableCell({ children: [new Paragraph('')] })]);

	// Build document sections
	const children = [
		new Paragraph({
			text: projectTitle,
			heading: HeadingLevel.TITLE,
			spacing: { after: 200 },
		}),
		new Paragraph({
			children: [
				new TextRun({
					text: `Generated: ${new Date().toLocaleString()}`,
					size: 20,
					color: '666666',
				}),
				new TextRun({ text: `  |  Analyst: ${userName}`, size: 20, color: '666666' }),
			],
			spacing: { after: 400 },
		}),
	];

	// Add map image if captured
	if (mapImageBuffer) {
		children.push(
			new Paragraph({
				text: 'Classification Map',
				heading: HeadingLevel.HEADING_1,
				spacing: { before: 200, after: 200 },
			}),
			new Paragraph({
				children: [
					new ImageRun({
						data: mapImageBuffer,
						transformation: {
							width: 600,
							height: 400,
						},
						type: 'png',
					}),
				],
				spacing: { after: 400 },
			})
		);
	}

	children.push(
		new Paragraph({
			text: 'Project Information',
			heading: HeadingLevel.HEADING_1,
			spacing: { before: 200, after: 200 },
		}),
		new DocxTable({
			width: { size: 100, type: WidthType.PERCENTAGE },
			rows: [
				new TableRow({
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Project Area', bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [new Paragraph(projectArea ? `${projectArea} ha` : 'N/A')],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({ text: 'Data Collection', bold: true }),
									],
								}),
							],
						}),
						new TableCell({ children: [new Paragraph(projectCreated || 'N/A')] }),
					],
				}),
				new TableRow({
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Hexagon Size', bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [new Paragraph(`${hexagonSize.toLocaleString()} m²`)],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Resolution', bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [new Paragraph(resolution ? `${resolution} cm/px` : 'N/A')],
						}),
					],
				}),
				new TableRow({
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({ text: 'Objects Detected', bold: true }),
									],
								}),
							],
						}),
						new TableCell({
							children: [new Paragraph(totalObjectCount.toLocaleString())],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Mean Height', bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [
								new Paragraph(overallMeanHeight ? `${overallMeanHeight} m` : 'N/A'),
							],
						}),
					],
				}),
				new TableRow({
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({ text: 'Detection Source', bold: true }),
									],
								}),
							],
						}),
						new TableCell({ children: [new Paragraph(modelDescription)] }),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({ text: labels.detectionClasses, bold: true }),
									],
								}),
							],
						}),
						new TableCell({ children: [new Paragraph(modelClasses)] }),
					],
				}),
			],
		})
	);

	// Add notes section if present
	if (projectNotes) {
		children.push(
			new Paragraph({
				text: 'Notes',
				heading: HeadingLevel.HEADING_1,
				spacing: { before: 400, after: 200 },
			}),
			new Paragraph({
				text: projectNotes,
				spacing: { after: 200 },
			})
		);
	}

	// Add detection statistics if present
	if (detectionStats.length > 0) {
		children.push(
			new Paragraph({
				text: 'Detection Statistics',
				heading: HeadingLevel.HEADING_1,
				spacing: { before: 400, after: 200 },
			}),
			new DocxTable({
				width: { size: 100, type: WidthType.PERCENTAGE },
				rows: [
					new TableRow({
						tableHeader: true,
						children: [
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({
												text: labels.detectionClass,
												bold: true,
											}),
										],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [new TextRun({ text: 'Count', bold: true })],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({ text: 'Distribution', bold: true }),
										],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({ text: 'Mean Height', bold: true }),
										],
									}),
								],
							}),
						],
					}),
					...detectionStats.map(
						stat =>
							new TableRow({
								children: [
									new TableCell({ children: [new Paragraph(stat.name)] }),
									new TableCell({
										children: [new Paragraph(stat.count.toLocaleString())],
									}),
									new TableCell({
										children: [new Paragraph(`${stat.percentage}%`)],
									}),
									new TableCell({
										children: [
											new Paragraph(
												stat.meanHeight ? `${stat.meanHeight} m` : '-'
											),
										],
									}),
								],
							})
					),
					new TableRow({
						children: [
							new TableCell({
								children: [
									new Paragraph({
										children: [new TextRun({ text: 'TOTAL', bold: true })],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({
												text: totalObjectCount.toLocaleString(),
												bold: true,
											}),
										],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [new TextRun({ text: '100%', bold: true })],
									}),
								],
							}),
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({
												text: overallMeanHeight
													? `${overallMeanHeight} m`
													: '-',
												bold: true,
											}),
										],
									}),
								],
							}),
						],
					}),
				],
			})
		);
	}

	// Add classification summary — preceded by a plain-language description of
	// what the classes represent (and, if applicable, a note about values
	// that fall outside the configured class ranges) so the summary table is
	// understandable without the in-app editor for context.
	children.push(
		new Paragraph({
			text: 'Classification Summary',
			heading: HeadingLevel.HEADING_1,
			spacing: { before: 400, after: 100 },
		}),
		new Paragraph({
			children: [
				new TextRun({
					text: `Classified by: ${classificationMethod}`,
					size: 18,
					color: '666666',
				}),
			],
			spacing: { after: pricingBasisNote || coverageGapMessage ? 100 : 200 },
		}),
		...(pricingBasisNote
			? [
					new Paragraph({
						children: [
							new TextRun({
								text: pricingBasisNote,
								size: 18,
								color: '666666',
							}),
						],
						spacing: { after: coverageGapMessage ? 100 : 200 },
					}),
				]
			: []),
		...(coverageGapMessage
			? [
					new Paragraph({
						children: [
							new TextRun({
								text: `⚠ ${coverageGapMessage}`,
								size: 18,
								color: 'B45309',
							}),
						],
						spacing: { after: 200 },
					}),
				]
			: []),
		new DocxTable({
			width: { size: 100, type: WidthType.PERCENTAGE },
			rows: [
				new TableRow({
					tableHeader: true,
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Class', bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'Count', bold: true })],
								}),
							],
						}),
						...containerAreaCells(() => [
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({ text: containerAreaLabel, bold: true }),
										],
									}),
								],
							}),
						]),
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: areaLabel, bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: percentageLabel, bold: true })],
								}),
							],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({ text: `${currency.symbol}/m²`, bold: true }),
									],
								}),
							],
						}),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({
											text: `Total (${currency.symbol})`,
											bold: true,
										}),
									],
								}),
							],
						}),
					],
				}),
				...summaryData.map(
					row =>
						new TableRow({
							children: [
								new TableCell({ children: [new Paragraph(row.label)] }),
								new TableCell({
									children: [new Paragraph(row.count.toLocaleString())],
								}),
								...containerAreaCells(() => [
									new TableCell({
										children: [
											new Paragraph(
												(row.containerArea || 0).toLocaleString()
											),
										],
									}),
								]),
								new TableCell({
									children: [new Paragraph(row.area.toLocaleString())],
								}),
								new TableCell({ children: [new Paragraph(`${row.percentage}%`)] }),
								new TableCell({
									children: [new Paragraph(row.pricePerM2.toFixed(2))],
								}),
								new TableCell({
									children: [
										new Paragraph(
											row.totalCost.toLocaleString(undefined, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})
										),
									],
								}),
							],
						})
				),
				...customPriceFields
					.filter(f => f.price > 0)
					.map(
						f =>
							new TableRow({
								children: [
									new TableCell({
										children: [
											new Paragraph({
												children: [
													new TextRun({
														text: f.label,
														italics: true,
													}),
												],
											}),
										],
									}),
									new TableCell({ children: [new Paragraph('')] }),
									...containerAreaGapCells(),
									new TableCell({
										children: [new Paragraph(totalArea.toLocaleString())],
									}),
									new TableCell({ children: [new Paragraph('')] }),
									new TableCell({
										children: [new Paragraph(f.price.toFixed(2))],
									}),
									new TableCell({
										children: [
											new Paragraph(
												f.cost.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})
											),
										],
									}),
								],
							})
					),
				new TableRow({
					children: [
						new TableCell({
							children: [
								new Paragraph({
									children: [new TextRun({ text: 'TOTAL', bold: true })],
								}),
							],
						}),
						new TableCell({ children: [new Paragraph('')] }),
						...containerAreaCells(() => [
							new TableCell({
								children: [
									new Paragraph({
										children: [
											new TextRun({
												text: `${totalContainerArea.toLocaleString()}`,
												bold: true,
											}),
										],
									}),
								],
							}),
						]),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({
											text: `${totalArea.toLocaleString()}`,
											bold: true,
										}),
									],
								}),
							],
						}),
						new TableCell({ children: [new Paragraph('')] }),
						new TableCell({ children: [new Paragraph('')] }),
						new TableCell({
							children: [
								new Paragraph({
									children: [
										new TextRun({
											text: formatCurrencyTotal(totalCost, currency),
											bold: true,
										}),
									],
								}),
							],
						}),
					],
				}),
			],
		}),
		new Paragraph({
			children: [
				new TextRun({
					text: '\n\nGenerated by Biodrone AI Portal',
					size: 18,
					color: '666666',
				}),
				new TextRun({
					text: `\n© ${new Date().getFullYear()} Biodrone AS - www.biodrone.no`,
					size: 18,
					color: '666666',
				}),
			],
			spacing: { before: 400 },
		})
	);

	const doc = new Document({
		sections: [
			{
				properties: {},
				children,
			},
		],
	});

	return doc;
};

/**
 * Export classification report to DOCX file
 * Generates and downloads a Word document with the report.
 *
 * @param {Object} exportData - Prepared export data from prepareExportData
 * @param {Uint8Array|null} mapImageBuffer - Map screenshot as buffer
 */
export const exportToDOCX = async (exportData, mapImageBuffer) => {
	const doc = await generateDOCXDocument(exportData, mapImageBuffer);
	const blob = await Packer.toBlob(doc);
	const projectTitle = exportData.projectTitle || 'Classification Report';
	saveAs(blob, `${projectTitle.replace(/[^a-z0-9]/gi, '_')}_report.docx`);
};
